import {Banner} from '@primer/react'

import {CopilotProgressIndicator} from '@github-ui/copilot-progress-indicator-ui'

import type {UsePullRequestSummaryResult} from './use-pull-request-summary'

export interface PullRequestSummaryBannerProps {
  summary: UsePullRequestSummaryResult
}

/**
 * Status banner shown in the description editor while a Copilot summary is generating, or
 * after it fails or recovers a stale result.
 *
 * Unlike the classic `CopilotSummaryBanner`, this intentionally omits the post-completion
 * feedback banner: that requires `feedbackUrl`/`classificationOptions` data this page doesn't
 * yet receive from the backend. It also can't reuse that package's presentational banners
 * directly -- `@github-ui/copilot-summary-banner` is an application-entrypoint package and
 * isn't allowed as a dependency of other packages -- so this renders its own minimal `Banner`.
 */
export function PullRequestSummaryBanner({summary}: PullRequestSummaryBannerProps) {
  const {isGenerating, errorMessage, isStale, dismissBanner} = summary

  if (isGenerating) return <CopilotProgressIndicator isLoading />

  if (errorMessage) {
    return (
      <Banner variant="critical" role="alert" onDismiss={dismissBanner}>
        <Banner.Title as="h3">Copilot summary error</Banner.Title>
        <span>{errorMessage}</span>
      </Banner>
    )
  }

  if (isStale) {
    return (
      <Banner variant="warning" role="alert" onDismiss={dismissBanner}>
        <Banner.Title as="h3">Copilot summary warning</Banner.Title>
        <span>Copilot couldn&rsquo;t regenerate this summary. The previous summary is shown.</span>
      </Banner>
    )
  }

  return null
}
